import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trash2, ExternalLink, MessageSquareText, Award, X, BadgeCheck, ShieldAlert,
} from "lucide-react";
import Seo, { breadcrumbs } from "../components/Seo";
import { compareStore, COMPARE_LIMIT } from "../lib/storage";
import { siteConfig, fmtMoney, fmtMiles, fees, disclaimers } from "../config";

// Fields to compare side-by-side. Each row = label + value-getter + optional
// `highlight: true` to apply min/max highlight styling.
const ROWS = [
  { label: "Price", get: (v) => v.price, fmt: fmtMoney, highlight: true, kind: "min" },
  { label: "MSRP", get: (v) => v.status === "new" ? v.msrp : null, fmt: fmtMoney },
  { label: "Final Price Before Taxes", get: (v) => (v.price || 0) + fees.docFee + fees.titleFee, fmt: fmtMoney, highlight: true, kind: "min" },
  { label: "Year", get: (v) => v.year, highlight: true, kind: "max" },
  { label: "Mileage", get: (v) => v.mileage, fmt: fmtMiles, highlight: true, kind: "min" },
  { label: "New / Used", get: (v) => v.status, fmt: (s) => s === "new" ? "New" : "Used" },
  { label: "Trim", get: (v) => v.trim },
  { label: "Body Style", get: (v) => v.body_style },
  { label: "Engine", get: (v) => v.engine, highlight: true, kind: "diff" },
  { label: "Transmission", get: (v) => v.transmission },
  { label: "Drivetrain", get: (v) => v.drivetrain, highlight: true, kind: "diff" },
  { label: "Fuel Type", get: (v) => v.fuel_type === "gas" ? "Gas" : v.fuel_type, highlight: true, kind: "diff" },
  { label: "City / Hwy MPG", get: (v) => (v.city_mpg && v.highway_mpg) ? `${v.city_mpg} / ${v.highway_mpg}` : null },
  { label: "EV Range", get: (v) => v.ev_range ? `${v.ev_range} mi` : null },
  { label: "Exterior Color", get: (v) => v.exterior_color },
  { label: "Interior Color", get: (v) => v.interior_color },
  { label: "VIN", get: (v) => v.vin },
  { label: "Stock #", get: (v) => v.stock_number },
];

// Pick the top key features (first ~5 from options).
const keyFeatures = (v) => (v.options || []).slice(0, 5);

function computeHighlights(items, row) {
  if (!row.highlight) return new Set();
  const values = items.map((v) => row.get(v)).filter((x) => x !== null && x !== undefined && x !== "");
  if (values.length < 2) return new Set();

  if (row.kind === "min" || row.kind === "max") {
    const nums = values.filter((n) => typeof n === "number");
    if (nums.length < 2) return new Set();
    const target = row.kind === "min" ? Math.min(...nums) : Math.max(...nums);
    const out = new Set();
    items.forEach((v, i) => {
      if (row.get(v) === target) out.add(i);
    });
    return out;
  }
  if (row.kind === "diff") {
    // Highlight cell when its value differs from any other.
    const set = new Set();
    items.forEach((v, i) => {
      const val = row.get(v);
      if (val == null || val === "") return;
      const differs = items.some((o, j) => j !== i && row.get(o) != null && row.get(o) !== val);
      if (differs) set.add(i);
    });
    return set;
  }
  return new Set();
}

export default function Compare() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const sync = () => setItems(compareStore.list());
    sync();
    window.addEventListener("ssc-storage", sync);
    return () => window.removeEventListener("ssc-storage", sync);
  }, []);

  const rowHighlights = useMemo(
    () => ROWS.map((r) => computeHighlights(items, r)),
    [items]
  );

  const titleOf = (v) => `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim();

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)] pb-24">
      <Seo
        title="Compare Vehicles | Scott Sells Chevys"
        description={`Compare up to ${COMPARE_LIMIT} new or used vehicles side by side — price, mileage, drivetrain, features, MPG, and more.`}
        canonicalPath="/compare"
        jsonLd={[breadcrumbs([{ name: "Home", path: "/" }, { name: "Compare", path: "/compare" }])]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-14">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Compare Vehicles</h1>
          <p className="mt-3 text-[var(--ssc-silver)]">
            Compare up to {COMPARE_LIMIT} vehicles side-by-side. Differences are highlighted to make the choice easier.
          </p>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          {items.length === 0 ? (
            <div className="ssc-card text-center max-w-2xl mx-auto" data-testid="compare-empty">
              <h2 className="text-xl font-extrabold text-[var(--ssc-navy)]">No vehicles in compare yet.</h2>
              <p className="text-[var(--ssc-ink-2)] mt-2">
                Add up to {COMPARE_LIMIT} vehicles from any vehicle card or detail page using the <span className="font-bold">Add to Compare</span> button.
              </p>
              <div className="mt-5 flex flex-wrap gap-2 justify-center">
                <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy">Shop New</Link>
                <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline">Shop Used</Link>
                <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary">
                  <MessageSquareText size={16} /> Text Scott
                </a>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-[var(--ssc-line)] bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-3 sticky left-0 bg-white z-10 border-r border-[var(--ssc-line)] min-w-[150px]"></th>
                      {items.map((v) => (
                        <th key={v.slug || v.id} className="p-3 text-left min-w-[240px] border-l border-[var(--ssc-line)] align-top">
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => compareStore.remove(v.slug)}
                              className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white border border-[var(--ssc-line)] hover:bg-red-50 hover:border-red-300 hover:text-red-600 text-[var(--ssc-ink-2)] grid place-items-center transition z-10"
                              aria-label={`Remove ${titleOf(v)}`}
                              data-testid={`cmp-remove-${v.stock_number}`}
                            >
                              <X size={14} />
                            </button>
                            <Link to={`/${v.status}-vehicles/${v.slug}`} className="block">
                              {v.photos?.[0]?.url ? (
                                <img
                                  src={v.photos[0].url}
                                  alt={titleOf(v)}
                                  className="w-full aspect-[16/10] object-cover rounded-xl"
                                />
                              ) : (
                                <div className="w-full aspect-[16/10] bg-[var(--ssc-silver-2)] rounded-xl grid place-items-center text-xs text-[var(--ssc-ink-2)]">
                                  Photo coming soon
                                </div>
                              )}
                              <div className="mt-2 flex items-center gap-2 flex-wrap">
                                {v.status === "new" ? (
                                  <span className="ssc-badge ssc-badge-new">New</span>
                                ) : (
                                  <span className="ssc-badge ssc-badge-used flex items-center gap-1" data-testid={`cmp-as-is-${v.stock_number}`}>
                                    <ShieldAlert size={11} /> Sold As-Is
                                  </span>
                                )}
                              </div>
                              <div className="mt-1.5 font-extrabold text-[var(--ssc-navy)] leading-tight">
                                {titleOf(v)}
                              </div>
                              <div className="text-xs text-[var(--ssc-ink-2)] mt-0.5">
                                Stock #{v.stock_number}
                              </div>
                            </Link>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ROWS.map((row, rIdx) => {
                      const highlights = rowHighlights[rIdx];
                      // Hide row if all values are empty
                      const anyVal = items.some((v) => {
                        const val = row.get(v);
                        return val !== null && val !== undefined && val !== "";
                      });
                      if (!anyVal) return null;
                      return (
                        <tr key={row.label} className="border-t border-[var(--ssc-line)]">
                          <td className="p-3 font-bold text-[var(--ssc-navy)] sticky left-0 bg-white z-[1] border-r border-[var(--ssc-line)] text-xs uppercase tracking-wide">
                            {row.label}
                          </td>
                          {items.map((v, i) => {
                            const raw = row.get(v);
                            const val = raw == null || raw === "" ? null : (row.fmt ? row.fmt(raw) : String(raw));
                            const hl = highlights.has(i);
                            return (
                              <td
                                key={v.slug || v.id}
                                className={`p-3 border-l border-[var(--ssc-line)] text-[var(--ssc-ink)] ${hl ? "bg-amber-50 font-bold text-[var(--ssc-navy)]" : ""}`}
                                data-testid={`cmp-row-${row.label.toLowerCase().replace(/[^a-z]+/g, "-")}-${i}`}
                              >
                                {val ?? <span className="text-[var(--ssc-ink-2)]/50">—</span>}
                                {hl && row.kind === "min" && (
                                  <BadgeCheck size={12} className="inline ml-1 text-emerald-600" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}

                    {/* Key Features row */}
                    <tr className="border-t border-[var(--ssc-line)]">
                      <td className="p-3 font-bold text-[var(--ssc-navy)] sticky left-0 bg-white z-[1] border-r border-[var(--ssc-line)] text-xs uppercase tracking-wide align-top">
                        Key Features
                      </td>
                      {items.map((v) => {
                        const feats = keyFeatures(v);
                        return (
                          <td key={v.slug || v.id} className="p-3 border-l border-[var(--ssc-line)] text-[var(--ssc-ink-2)] align-top">
                            {feats.length ? (
                              <ul className="text-xs space-y-1">
                                {feats.map((f, i) => (
                                  <li key={i} className="flex gap-1.5"><span className="text-[var(--ssc-blue)]">•</span><span>{f}</span></li>
                                ))}
                              </ul>
                            ) : <span className="text-[var(--ssc-ink-2)]/50">—</span>}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Disclosure */}
                    <tr className="border-t border-[var(--ssc-line)]">
                      <td className="p-3 font-bold text-[var(--ssc-navy)] sticky left-0 bg-white z-[1] border-r border-[var(--ssc-line)] text-xs uppercase tracking-wide align-top">
                        Notes
                      </td>
                      {items.map((v) => (
                        <td key={v.slug || v.id} className="p-3 border-l border-[var(--ssc-line)] text-[11px] text-[var(--ssc-ink-2)] align-top">
                          {v.status === "used" ? (
                            <span className="block">
                              <span className="font-bold text-[var(--ssc-navy)]">Sold As-Is.</span>{" "}
                              Pre-owned vehicles are sold as-is. No implied warranty unless separately written.
                            </span>
                          ) : (
                            <span>Includes manufacturer&apos;s new-vehicle warranty per Chevrolet program terms.</span>
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* Actions row */}
                    <tr className="border-t border-[var(--ssc-line)] bg-[var(--ssc-paper)]">
                      <td className="p-3 font-bold text-[var(--ssc-navy)] sticky left-0 bg-[var(--ssc-paper)] z-[1] border-r border-[var(--ssc-line)] text-xs uppercase tracking-wide align-top">
                        Actions
                      </td>
                      {items.map((v) => {
                        const t = titleOf(v);
                        const sms = `${siteConfig.scott.phoneSms}?&body=${encodeURIComponent(`Hi Scott, I'd like to compare Stock ${v.stock_number} — ${t}.`)}`;
                        const detailHref = `/${v.status}-vehicles/${v.slug}`;
                        return (
                          <td key={v.slug || v.id} className="p-3 border-l border-[var(--ssc-line)] align-top">
                            <div className="space-y-2">
                              <a href={sms} className="ssc-btn ssc-btn-primary w-full !py-1.5 !text-xs" data-testid={`cmp-text-${v.stock_number}`}>
                                <MessageSquareText size={14} /> Text Scott
                              </a>
                              <Link
                                to={`/scotts-deal-desk?vin=${v.vin}&stock=${v.stock_number}&title=${encodeURIComponent(t)}&status=${v.status}&url=${encodeURIComponent(detailHref)}`}
                                className="ssc-btn ssc-btn-navy w-full !py-1.5 !text-xs"
                                data-testid={`cmp-byod-${v.stock_number}`}
                              >
                                <Award size={14} /> Build My Deal
                              </Link>
                              <Link
                                to={detailHref}
                                className="ssc-btn ssc-btn-outline w-full !py-1.5 !text-xs"
                                data-testid={`cmp-vdp-${v.stock_number}`}
                              >
                                <ExternalLink size={14} /> View Page
                              </Link>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="p-4 border-t border-[var(--ssc-line)] bg-[var(--ssc-paper)] flex flex-wrap items-center gap-3 justify-between">
                <div className="text-xs text-[var(--ssc-ink-2)]">
                  Comparing <span className="font-bold text-[var(--ssc-navy)] tabular-nums">{items.length}</span> of {COMPARE_LIMIT} vehicles
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => compareStore.clear()}
                    className="ssc-btn ssc-btn-outline !py-1.5 !text-xs"
                    data-testid="cmp-clear-all"
                  >
                    <Trash2 size={14} /> Clear All
                  </button>
                  <Link to="/new-vehicles" className="ssc-btn ssc-btn-navy !py-1.5 !text-xs">
                    Add More New
                  </Link>
                  <Link to="/used-vehicles" className="ssc-btn ssc-btn-navy !py-1.5 !text-xs">
                    Add More Used
                  </Link>
                </div>
              </div>
            </div>
          )}

          <p className="text-[11px] text-[var(--ssc-ink-2)] mt-5 max-w-3xl">
            {disclaimers.inventory}
          </p>
        </div>
      </section>
    </main>
  );
}
